// Developer error-capture overlay — desktop CRM dev tool.
//
// Renders a small fixed pill in the bottom-right corner that expands into a
// scrollable panel listing every captured error. Gated by a per-user flag
// (`user.dev_error_overlay_enabled`); the parent App is responsible for
// conditional mounting.
//
// Position: fixed bottom-right. The production toast lives at top-right
// (z-[200]) so the two never overlap. We use z-[9999] — yes, that's above
// most modals (z-[60..100]) in this codebase, but this is a *developer* tool
// and is gated by an opt-in user flag; visibility wins over modal layering
// (the only other z-[9999] in the codebase is `CenterTransitionOverlay`, an
// intentional top-of-stack splash).
//
// All capture hooks install on mount and uninstall on unmount — when the
// user logs out or the flag flips off mid-session, every monkey-patch is
// restored cleanly (see `capture.ts`).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { installCapture, buildAdHocEntry } from "./capture";
import { LABEL_SEVERITY, type DevErrorEntry, type DevErrorLabel } from "./types";

const MAX_ENTRIES = 200;
const Z_INDEX = 9999;

// ---------------------------------------------------------------------------
// Global emitter — so `recordDevError()` works even before the component
// has mounted (callers don't have to thread a ref). We keep a module-level
// queue that flushes once a component subscribes.
// ---------------------------------------------------------------------------
type Listener = (e: Omit<DevErrorEntry, "id" | "timestamp">) => void;
let listener: Listener | null = null;
const pending: Omit<DevErrorEntry, "id" | "timestamp">[] = [];

function emit(entry: Omit<DevErrorEntry, "id" | "timestamp">) {
  if (listener) listener(entry);
  else pending.push(entry);
}

/**
 * Ad-hoc reporting helper. Safe to call from anywhere — entries queue until
 * the overlay is mounted, then drain into the list.
 */
export function recordDevError(
  label: DevErrorLabel | undefined,
  message: string,
  details?: Record<string, unknown>,
): void {
  emit(buildAdHocEntry(label, message, details));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTime(ms: number, withMs = false): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  if (!withMs) return `${hh}:${mm}:${ss}`;
  const mss = String(d.getMilliseconds()).padStart(3, "0");
  return `${hh}:${mm}:${ss}:${mss}`;
}

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatEntryForCopy(e: DevErrorEntry): string {
  const detailsBlock = (() => {
    try {
      return JSON.stringify(e.details, null, 2);
    } catch {
      return String(e.details);
    }
  })();
  return `[${e.label}] [${formatTime(e.timestamp, true)}] ${e.title}\n\n${detailsBlock}`;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    /* fall through to textarea fallback */
  }
  // Fallback for non-secure contexts / older browsers.
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}

// ---------------------------------------------------------------------------
// Visual tokens — derived from theme CSS custom properties (HSL triplets).
// Severity colors use the existing --danger and --warning-bg tokens, while
// chrome (panel bg / border / text) tracks the active theme via --surface,
// --border, --text, --muted. No hardcoded Tailwind colors anywhere.
// ---------------------------------------------------------------------------
function badgeStyle(label: DevErrorLabel): React.CSSProperties {
  const sev = LABEL_SEVERITY[label];
  if (sev === "error") {
    return {
      background: "hsl(var(--dangerBg))",
      color: "hsl(var(--dangerText))",
      border: "1px solid hsl(var(--danger) / 0.4)",
    };
  }
  return {
    background: "hsl(var(--warning-bg))",
    color: "hsl(var(--warning-text))",
    border: "1px solid hsl(var(--warning-text) / 0.25)",
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function DevErrorOverlay() {
  const [entries, setEntries] = useState<DevErrorEntry[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  // Use a ref-bound add() so the capture install hook never has to re-run.
  const addEntry = useCallback((raw: Omit<DevErrorEntry, "id" | "timestamp">) => {
    setEntries((prev) => {
      const next: DevErrorEntry = { ...raw, id: makeId(), timestamp: Date.now() };
      const combined = [next, ...prev];
      return combined.length > MAX_ENTRIES ? combined.slice(0, MAX_ENTRIES) : combined;
    });
  }, []);

  // Subscribe the module-level emitter to this component and flush any
  // entries that were queued before mount. Also wire up the capture hooks.
  // Single effect so install/uninstall + subscribe/unsubscribe stay in sync.
  const addEntryRef = useRef(addEntry);
  addEntryRef.current = addEntry;

  useEffect(() => {
    const sub: Listener = (e) => addEntryRef.current(e);
    listener = sub;
    // Drain anything queued before mount.
    if (pending.length > 0) {
      const drained = pending.splice(0, pending.length);
      for (const e of drained) sub(e);
    }

    const handle = installCapture((e) => addEntryRef.current(e));

    return () => {
      handle.uninstall();
      if (listener === sub) listener = null;
    };
  }, []);

  const counts = useMemo(() => {
    let errors = 0;
    let warns = 0;
    for (const e of entries) {
      if (LABEL_SEVERITY[e.label] === "error") errors += 1;
      else warns += 1;
    }
    return { errors, warns, total: entries.length };
  }, [entries]);

  const latestLabel: DevErrorLabel | null = entries[0]?.label ?? null;
  const hasErrors = counts.errors > 0;

  // ---- collapsed pill --------------------------------------------------
  if (!expanded) {
    const pillBg = hasErrors ? "hsl(var(--danger))" : counts.warns > 0 ? "hsl(var(--warning-text))" : "hsl(var(--surface2))";
    const pillFg = hasErrors || counts.warns > 0 ? "hsl(var(--accentText))" : "hsl(var(--text))";
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label={`Dev errors: ${counts.total} captured`}
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: Z_INDEX,
          background: pillBg,
          color: pillFg,
          borderRadius: 999,
          padding: "6px 12px",
          fontSize: 12,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 6px 16px hsl(var(--shadow) / 0.18)",
          border: "1px solid hsl(var(--border))",
          cursor: "pointer",
          lineHeight: 1.2,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "currentColor",
            opacity: 0.85,
          }}
        />
        <span style={{ fontWeight: 600 }}>{counts.total}</span>
        <span style={{ opacity: 0.85 }}>{latestLabel ?? "DEV"}</span>
      </button>
    );
  }

  // ---- expanded panel --------------------------------------------------
  return (
    <div
      role="dialog"
      aria-label="Dev Error Overlay"
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: Z_INDEX,
        width: 480,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "60vh",
        background: "hsl(var(--surface))",
        color: "hsl(var(--text))",
        border: "1px solid hsl(var(--border))",
        borderRadius: 12,
        boxShadow: "0 18px 48px hsl(var(--shadow) / 0.25)",
        display: "flex",
        flexDirection: "column",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          borderBottom: "1px solid hsl(var(--border))",
          background: "hsl(var(--surface2))",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Dev Error Overlay</div>
          <div style={{ color: "hsl(var(--muted))", fontSize: 11 }}>
            {counts.total} captured
            {counts.errors > 0 ? ` · ${counts.errors} error${counts.errors === 1 ? "" : "s"}` : ""}
            {counts.warns > 0 ? ` · ${counts.warns} warn${counts.warns === 1 ? "" : "s"}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <HeaderButton
            disabled={entries.length === 0}
            onClick={() => {
              void copyToClipboard(entries.map(formatEntryForCopy).join("\n\n---\n\n"));
            }}
          >
            Copy all
          </HeaderButton>
          <HeaderButton
            disabled={entries.length === 0}
            onClick={() => {
              setEntries([]);
              setOpenRows(new Set());
            }}
          >
            Clear
          </HeaderButton>
          <HeaderButton onClick={() => setExpanded(false)} aria-label="Collapse">
            ✕
          </HeaderButton>
        </div>
      </div>

      {/* List */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {entries.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "hsl(var(--muted))",
              fontSize: 12,
            }}
          >
            No errors captured yet.
          </div>
        ) : (
          entries.map((e) => {
            const open = openRows.has(e.id);
            return (
              <div
                key={e.id}
                style={{
                  borderBottom: "1px solid hsl(var(--border))",
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setOpenRows((prev) => {
                      const next = new Set(prev);
                      if (next.has(e.id)) next.delete(e.id);
                      else next.add(e.id);
                      return next;
                    });
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setOpenRows((prev) => {
                        const next = new Set(prev);
                        if (next.has(e.id)) next.delete(e.id);
                        else next.add(e.id);
                        return next;
                      });
                    }
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto 1fr auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      ...badgeStyle(e.label),
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: 0.4,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.label}
                  </span>
                  <span style={{ color: "hsl(var(--muted))", fontSize: 11, whiteSpace: "nowrap" }}>
                    {formatTime(e.timestamp)}
                  </span>
                  <span
                    title={e.title}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                    }}
                  >
                    {e.title}
                  </span>
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      void copyToClipboard(formatEntryForCopy(e));
                    }}
                    style={{
                      background: "transparent",
                      color: "hsl(var(--accent))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Copy
                  </button>
                </div>
                {open ? (
                  <pre
                    style={{
                      margin: 0,
                      padding: "8px 12px 12px",
                      background: "hsl(var(--surface2))",
                      color: "hsl(var(--text))",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 11,
                      lineHeight: 1.45,
                      borderTop: "1px solid hsl(var(--border))",
                    }}
                  >
                    {(() => {
                      try {
                        return JSON.stringify(e.details, null, 2);
                      } catch {
                        return String(e.details);
                      }
                    })()}
                  </pre>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local subcomponents
// ---------------------------------------------------------------------------
function HeaderButton(props: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props["aria-label"]}
      style={{
        background: "hsl(var(--surface))",
        color: "hsl(var(--text))",
        border: "1px solid hsl(var(--border))",
        borderRadius: 6,
        padding: "3px 8px",
        fontSize: 11,
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.5 : 1,
        fontFamily: "inherit",
      }}
    >
      {props.children}
    </button>
  );
}
